import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Copy, ExternalLink, List, Loader2, Phone, Plus, Reply, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { cn } from "@/lib/utils";
import { AgendaRatingsPanel } from "@/components/agenda/agenda-ratings-panel";
import {
  AGENDA_MESSAGE_KIND_LABELS,
  gpsDriveAgendaMessagesService,
  type AgendaMessageKind,
  type AgendaMessageOption,
  type AgendaMessageOptionKind,
  type AgendaMessageSettings,
  type AgendaMessageTemplate,
  type AgendaMessageType,
} from "@/services/gpsdrive-agenda-messages.service";

export const Route = createFileRoute("/admin/agenda-messages")({
  component: () => (
    <AdminAccessGate action="comunicacao">
      <AgendaMessagesPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Mensagens de Agendamento — Administração" }] }),
});

// Espelha AGENDA_MESSAGE_VARIABLES no backend
// (src/agenda-messages/agenda-messages-template.ts) — variável nova entra
// nos dois lugares.
const VARIABLES: { key: string; label: string; example: string }[] = [
  { key: "cliente", label: "Nome do cliente", example: "Cliente Teste" },
  { key: "data", label: "Data do agendamento", example: "21/10/2026" },
  { key: "horario", label: "Loja: \"às 14:30\" · Externo: \"no turno da manhã (das 8h às 12h)\"", example: "às 14:30" },
  { key: "hora", label: "Loja: hora marcada · Externo: turno", example: "14:30" },
  { key: "turno", label: "Externo: manhã/tarde (vazio na loja)", example: "" },
  { key: "dia_semana", label: "Dia da semana", example: "quarta-feira" },
  { key: "tecnico", label: "Técnico", example: "Carlos" },
  { key: "servico", label: "Serviço (tópico)", example: "Instalação" },
  { key: "local", label: "Na loja / Atendimento externo", example: "Atendimento externo" },
  { key: "endereco", label: "Endereço do atendimento", example: "Av. Brasil, 100 - Centro, Porto Alegre/RS" },
  { key: "veiculo", label: "Veículo", example: "Fiat Toro" },
  { key: "placa", label: "Placa", example: "ABC1D23" },
  { key: "empresa", label: "Nome da empresa", example: "Sua Empresa" },
  { key: "observacao", label: "Observação do agendamento", example: "Levar documento do veículo" },
  { key: "causa", label: "Causa do encerramento", example: "Instalação concluída" },
  { key: "observacao_tecnico", label: "Observação do técnico", example: "Rastreador embaixo do painel" },
];

const KINDS: AgendaMessageKind[] = ["new", "day_of", "finished"];

const KIND_HELP: Record<AgendaMessageKind, string> = {
  new: "Enviada logo que o agendamento é criado.",
  day_of: "Enviada no dia do agendamento, no horário escolhido abaixo. Agendamento criado no próprio dia não recebe (a de novo agendamento já avisou).",
  finished: "Enviada quando o técnico encerra o atendimento.",
};

const TYPE_LABELS: Record<AgendaMessageType, string> = { text: "Só texto", button: "Botões", list: "Lista" };

const OPTION_KIND_LABELS: Record<AgendaMessageOptionKind, string> = {
  reply: "Resposta",
  url: "Abrir link",
  call: "Ligar",
  copy: "Copiar código",
};

const OPTION_VALUE_PLACEHOLDER: Record<AgendaMessageOptionKind, string> = {
  reply: "",
  url: "https://…",
  call: "+5551999999999",
  copy: "Código a copiar",
};

const emptyOption = (): AgendaMessageOption => ({
  kind: "reply",
  label: "",
  value: "",
  description: "",
  section: "",
  tag: "",
  score: null,
});

// Loja tem hora marcada; externo é por turno (pedido 2026-10-20).
type PreviewPlace = "loja" | "externa";
const EXTERNAL_EXAMPLES: Record<string, string> = {
  horario: "no turno da manhã (das 8h às 12h)",
  hora: "turno da manhã (das 8h às 12h)",
  turno: "manhã",
  local: "Atendimento externo",
};
const STORE_EXAMPLES: Record<string, string> = { local: "Na loja", endereco: "Na loja" };

// Mesma regra do backend: linha cujas variáveis ficaram todas vazias some.
function renderPreview(text: string, place: PreviewPlace = "loja"): string {
  const value = (key: string) =>
    (place === "externa" ? EXTERNAL_EXAMPLES[key] : STORE_EXAMPLES[key]) ??
    VARIABLES.find((v) => v.key === key)?.example ??
    "";
  return text
    .split("\n")
    .flatMap((line) => {
      const keys = [...line.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]);
      if (keys.length > 0 && keys.every((k) => !value(k))) return [];
      return [line.replace(/\{\{(\w+)\}\}/g, (_, k: string) => value(k))];
    })
    .join("\n");
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof GpsDriveApiError ? err.message : fallback;
}

// Mesmas regras do backend (agenda-messages.dto.ts), pra avisar antes de salvar.
function validate(t: AgendaMessageTemplate, label: string): string | null {
  if (!t.text.trim()) return `${label}: informe o texto da mensagem.`;
  if (t.type === "button") {
    if (t.options.length < 1 || t.options.length > 3) return `${label}: use de 1 a 3 botões.`;
    for (const o of t.options) {
      if (!o.label.trim()) return `${label}: todo botão precisa de texto.`;
      if (o.label.trim().length > 20) return `${label}: texto do botão "${o.label}" passa de 20 caracteres.`;
      if (o.kind !== "reply" && !o.value.trim()) return `${label}: informe o link/telefone/código do botão "${o.label}".`;
    }
  }
  if (t.type === "list") {
    if (t.options.length < 1) return `${label}: informe ao menos 1 item na lista.`;
    if (!t.listButton.trim()) return `${label}: informe o texto do botão que abre a lista.`;
    if (t.options.some((o) => !o.label.trim())) return `${label}: todo item precisa de texto.`;
  }
  return null;
}

function AgendaMessagesPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "agenda-messages"],
    queryFn: () => gpsDriveAgendaMessagesService.get(),
    retry: false,
  });
  const [form, setForm] = useState<AgendaMessageSettings | null>(null);
  const [tab, setTab] = useState<AgendaMessageKind | "replies" | "ratings">("new");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (settings.data && !form) setForm(settings.data);
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (input: AgendaMessageSettings) => gpsDriveAgendaMessagesService.update(input),
    onSuccess: (data) => {
      setSaveError(null);
      setSavedAt(Date.now());
      setForm(data);
      qc.setQueryData(["admin", "agenda-messages"], data);
    },
    onError: (err) => setSaveError(errorText(err, "Não foi possível salvar.")),
  });

  if (settings.isLoading || (!form && !settings.isError)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (settings.isError || !form || !settings.data) {
    return <p className="p-6 text-sm text-destructive">Não foi possível carregar a configuração.</p>;
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(settings.data);
  const setTemplate = (kind: AgendaMessageKind, t: AgendaMessageTemplate) =>
    setForm((f) => (f ? { ...f, templates: { ...f.templates, [kind]: t } } : f));

  const handleSave = () => {
    for (const k of KINDS) {
      const problem = validate(form.templates[k], AGENDA_MESSAGE_KIND_LABELS[k]);
      if (problem) {
        setSaveError(problem);
        setTab(k);
        return;
      }
    }
    setSaveError(null);
    save.mutate(form);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mensagens de Agendamento</h1>
        <p className="text-sm text-muted-foreground">
          WhatsApp automático para o cliente da Agenda: quando o agendamento é criado, no dia e quando o
          técnico encerra. Pode ter botões ou lista, e as respostas do cliente ficam registradas no agendamento.
        </p>
      </div>

      <GpsDriveSessionBanner />

      <Tabs value={tab} onValueChange={(v) => setTab(v as AgendaMessageKind | "replies" | "ratings")}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          {KINDS.map((k) => (
            <TabsTrigger key={k} value={k} className="gap-2">
              {AGENDA_MESSAGE_KIND_LABELS[k]}
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  form.templates[k].enabled ? "bg-emerald-500" : "bg-muted-foreground/30",
                )}
              />
            </TabsTrigger>
          ))}
          <TabsTrigger value="replies">Respostas recebidas</TabsTrigger>
          <TabsTrigger value="ratings">Avaliações</TabsTrigger>
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k} value={k} className="pt-2">
            <TemplateEditor
              kind={k}
              template={form.templates[k]}
              onChange={(t) => setTemplate(k, t)}
              dayOfTime={form.dayOfTime}
              onDayOfTimeChange={(v) => setForm((f) => (f ? { ...f, dayOfTime: v } : f))}
            />
          </TabsContent>
        ))}

        <TabsContent value="replies" className="pt-2">
          <RepliesTab />
        </TabsContent>

        <TabsContent value="ratings" className="pt-2">
          <AgendaRatingsPanel />
        </TabsContent>
      </Tabs>

      {tab !== "replies" && tab !== "ratings" && (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t bg-background/95 py-3 backdrop-blur">
          {saveError && <span className="text-sm text-destructive">{saveError}</span>}
          {!saveError && savedAt && !dirty && <span className="text-sm text-muted-foreground">Salvo.</span>}
          <Button variant="outline" disabled={!dirty || save.isPending} onClick={() => setForm(settings.data!)}>
            Descartar
          </Button>
          <Button disabled={!dirty || save.isPending} onClick={handleSave}>
            {save.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({
  kind,
  template: t,
  onChange,
  dayOfTime,
  onDayOfTimeChange,
}: {
  kind: AgendaMessageKind;
  template: AgendaMessageTemplate;
  onChange: (t: AgendaMessageTemplate) => void;
  dayOfTime: string;
  onDayOfTimeChange: (v: string) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const set = <K extends keyof AgendaMessageTemplate>(key: K, value: AgendaMessageTemplate[K]) =>
    onChange({ ...t, [key]: value });
  const setOption = (i: number, patch: Partial<AgendaMessageOption>) =>
    set(
      "options",
      t.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    );
  const moveOption = (i: number, dir: -1 | 1) => {
    const next = [...t.options];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set("options", next);
  };
  const maxOptions = t.type === "button" ? 3 : 10;

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    const el = textareaRef.current;
    if (!el) return set("text", t.text + token);
    const start = el.selectionStart ?? t.text.length;
    const end = el.selectionEnd ?? t.text.length;
    set("text", t.text.slice(0, start) + token + t.text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const changeType = (type: AgendaMessageType) => {
    // Lista só aceita itens de resposta; botões no máximo 3.
    let options = t.options;
    if (type === "list") options = options.map((o) => ({ ...o, kind: "reply" as const }));
    if (type === "button") options = options.slice(0, 3);
    if (type !== "text" && options.length === 0) options = [emptyOption()];
    onChange({ ...t, type, options, listButton: type === "list" && !t.listButton ? "Ver opções" : t.listButton });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="space-y-6">
        <Card className="rounded-2xl shadow-sm">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">{AGENDA_MESSAGE_KIND_LABELS[kind]}</CardTitle>
                <CardDescription>{KIND_HELP[kind]}</CardDescription>
              </div>
              <Switch checked={t.enabled} onCheckedChange={(v) => set("enabled", v)} />
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {kind === "day_of" && (
              <div className="space-y-2">
                <Label htmlFor="day-of-time">Horário de envio</Label>
                <Input
                  id="day-of-time"
                  type="time"
                  value={dayOfTime}
                  onChange={(e) => onDayOfTimeChange(e.target.value)}
                  className="max-w-[140px]"
                />
                <p className="text-xs text-muted-foreground">
                  Vai para todos os agendamentos do dia que ainda não começaram (horário de Brasília).
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Formato</Label>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(TYPE_LABELS) as AgendaMessageType[]).map((type) => (
                  <Button
                    key={type}
                    type="button"
                    size="sm"
                    variant={t.type === type ? "default" : "outline"}
                    onClick={() => changeType(type)}
                  >
                    {TYPE_LABELS[type]}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Variáveis</Label>
              <div className="flex flex-wrap gap-1.5">
                {VARIABLES.map((v) => (
                  <Badge
                    key={v.key}
                    variant="secondary"
                    className="cursor-pointer select-none font-mono text-[11px] hover:bg-secondary/70"
                    onClick={() => insertVariable(v.key)}
                    title={v.label}
                  >
                    {`{{${v.key}}}`}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Clique para inserir no texto. Use <b>{"{{horario}}"}</b> para o quando: na loja sai a hora marcada e no
                externo sai o turno. Linha com variável vazia (ex.: sem endereço) é removida.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`text-${kind}`}>Mensagem</Label>
              <Textarea
                id={`text-${kind}`}
                ref={textareaRef}
                value={t.text}
                onChange={(e) => set("text", e.target.value)}
                rows={7}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">*negrito*, _itálico_ e emojis funcionam como no WhatsApp.</p>
            </div>

            {t.type !== "text" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`footer-${kind}`}>Rodapé (opcional)</Label>
                  <Input
                    id={`footer-${kind}`}
                    value={t.footerText}
                    maxLength={60}
                    onChange={(e) => set("footerText", e.target.value)}
                    placeholder="{{empresa}}"
                  />
                </div>
                {t.type === "list" && (
                  <div className="space-y-2">
                    <Label htmlFor={`listbtn-${kind}`}>Texto do botão que abre a lista</Label>
                    <Input
                      id={`listbtn-${kind}`}
                      value={t.listButton}
                      maxLength={20}
                      onChange={(e) => set("listButton", e.target.value)}
                      placeholder="Ver opções"
                    />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {t.type !== "text" && (
          <Card className="rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">{t.type === "button" ? "Botões" : "Itens da lista"}</CardTitle>
              <CardDescription>
                {t.type === "button"
                  ? "Até 3 botões (texto até 20 caracteres). Botões de resposta são registrados quando o cliente toca."
                  : "Até 10 itens (texto até 24 caracteres). A seção agrupa itens; deixe em branco para continuar na anterior."}{" "}
                A <b>etiqueta</b> aparece no agendamento quando o cliente escolhe a opção (vazia = o próprio texto).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {t.options.map((o, i) => (
                <div key={o.id ?? `new-${i}`} className="space-y-3 rounded-xl border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-muted-foreground">
                      {t.type === "button" ? "Botão" : "Item"} {i + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => moveOption(i, -1)}>
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={i === t.options.length - 1}
                        onClick={() => moveOption(i, 1)}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => set("options", t.options.filter((_, idx) => idx !== i))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {t.type === "button" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={o.kind} onValueChange={(v) => setOption(i, { kind: v as AgendaMessageOptionKind })}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(Object.keys(OPTION_KIND_LABELS) as AgendaMessageOptionKind[]).map((k) => (
                              <SelectItem key={k} value={k}>
                                {OPTION_KIND_LABELS[k]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {t.type === "list" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Seção</Label>
                        <Input
                          className="h-9"
                          value={o.section}
                          maxLength={24}
                          onChange={(e) => setOption(i, { section: e.target.value })}
                          placeholder={i === 0 ? "Opções" : "(mesma da anterior)"}
                        />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Texto</Label>
                      <Input
                        className="h-9"
                        value={o.label}
                        maxLength={t.type === "button" ? 20 : 24}
                        onChange={(e) => setOption(i, { label: e.target.value })}
                      />
                    </div>
                    {t.type === "button" && o.kind !== "reply" && (
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">
                          {o.kind === "url" ? "Link" : o.kind === "call" ? "Telefone" : "Código"}
                        </Label>
                        <Input
                          className="h-9"
                          value={o.value}
                          onChange={(e) => setOption(i, { value: e.target.value })}
                          placeholder={OPTION_VALUE_PLACEHOLDER[o.kind]}
                        />
                      </div>
                    )}
                    {t.type === "list" && (
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Descrição (opcional)</Label>
                        <Input
                          className="h-9"
                          value={o.description}
                          maxLength={72}
                          onChange={(e) => setOption(i, { description: e.target.value })}
                        />
                      </div>
                    )}
                    {o.kind === "reply" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Nota (aba Avaliações)</Label>
                        <Select
                          value={o.score ? String(o.score) : "none"}
                          onValueChange={(v) => setOption(i, { score: v === "none" ? null : Number(v) })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Não é avaliação</SelectItem>
                            {[5, 4, 3, 2, 1].map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {"⭐".repeat(n)} {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {o.kind === "reply" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Etiqueta no agendamento</Label>
                        <Input
                          className="h-9"
                          value={o.tag}
                          maxLength={40}
                          onChange={(e) => setOption(i, { tag: e.target.value })}
                          placeholder={o.label ? `Ex.: Cliente escolheu "${o.label}"` : "Ex.: Cliente confirmou"}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={t.options.length >= maxOptions}
                onClick={() => set("options", [...t.options, emptyOption()])}
              >
                <Plus className="h-4 w-4" />
                {t.type === "button" ? "Adicionar botão" : "Adicionar item"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="space-y-6">
        <WhatsappPreview template={t} />
        <TestSendCard template={t} />
      </div>
    </div>
  );
}

const OPTION_ICON: Record<AgendaMessageOptionKind, typeof Reply> = {
  reply: Reply,
  url: ExternalLink,
  call: Phone,
  copy: Copy,
};

function WhatsappPreview({ template: t }: { template: AgendaMessageTemplate }) {
  const [place, setPlace] = useState<PreviewPlace>("loja");
  const text = renderPreview(t.text, place);
  const footer = renderPreview(t.footerText, place);
  return (
    <Card className="rounded-2xl shadow-sm lg:sticky lg:top-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Pré-visualização</CardTitle>
        <CardDescription>Com dados de exemplo.</CardDescription>
        <div className="flex gap-1.5 pt-1">
          {(["loja", "externa"] as const).map((p) => (
            <Button key={p} type="button" size="sm" variant={place === p ? "default" : "outline"} className="h-7 text-xs" onClick={() => setPlace(p)}>
              {p === "loja" ? "Na loja (horário)" : "Externo (turno)"}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl bg-[#e5ddd5] p-3 dark:bg-[#0b141a]">
          <div className="max-w-[290px] overflow-hidden rounded-lg bg-white shadow-sm dark:bg-[#202c33]">
            <div className="whitespace-pre-wrap break-words px-3 pb-1 pt-2 text-[13px] leading-snug text-slate-900 dark:text-slate-100">
              {text || "—"}
            </div>
            {t.type !== "text" && footer && (
              <div className="px-3 pb-1 text-[11px] text-slate-500 dark:text-slate-400">{footer}</div>
            )}
            <div className="px-3 pb-1.5 text-right text-[10px] text-slate-400">10:00</div>
            {t.type === "button" &&
              t.options.map((o, i) => {
                const Icon = OPTION_ICON[o.kind];
                return (
                  <div
                    key={i}
                    className="flex items-center justify-center gap-1.5 border-t border-slate-200 py-2 text-[13px] font-medium text-sky-600 dark:border-slate-700 dark:text-sky-400"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {o.label || "…"}
                  </div>
                );
              })}
            {t.type === "list" && (
              <div className="flex items-center justify-center gap-1.5 border-t border-slate-200 py-2 text-[13px] font-medium text-sky-600 dark:border-slate-700 dark:text-sky-400">
                <List className="h-3.5 w-3.5" />
                {t.listButton || "Ver opções"}
              </div>
            )}
          </div>
          {t.type === "list" && t.options.length > 0 && (
            <div className="mt-2 max-w-[290px] rounded-lg bg-white p-2 text-[12px] shadow-sm dark:bg-[#202c33]">
              {t.options.map((o, i) => {
                const section = o.section.trim();
                const prevSection = t.options.slice(0, i).reduce((s, p) => p.section.trim() || s, "");
                const showSection = i === 0 || (section && section !== prevSection);
                return (
                  <div key={i}>
                    {showSection && (
                      <div className="px-1 pb-0.5 pt-1 text-[10px] font-semibold uppercase text-emerald-700 dark:text-emerald-400">
                        {section || "Opções"}
                      </div>
                    )}
                    <div className="rounded px-1 py-1 text-slate-900 dark:text-slate-100">
                      {o.label || "…"}
                      {o.description && <div className="text-[11px] text-slate-500">{o.description}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TestSendCard({ template }: { template: AgendaMessageTemplate }) {
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const test = useMutation({
    mutationFn: () => gpsDriveAgendaMessagesService.testSend({ phone: phone.trim(), template: { ...template, enabled: true } }),
    onSuccess: () => setMsg({ ok: true, text: "Enviada." }),
    onError: (err) => setMsg({ ok: false, text: errorText(err, "Falha ao enviar o teste.") }),
  });
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Testar envio</CardTitle>
        <CardDescription>Manda esta mensagem (mesmo sem salvar) com dados de exemplo.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input type="tel" placeholder="(51) 99999-9999" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <div className="flex items-center justify-end gap-3">
          {msg && <span className={cn("text-xs", msg.ok ? "text-emerald-600" : "text-destructive")}>{msg.text}</span>}
          <Button
            variant="outline"
            disabled={test.isPending || phone.trim().length < 8}
            onClick={() => {
              setMsg(null);
              const problem = validate(template, "Mensagem");
              if (problem) return setMsg({ ok: false, text: problem });
              test.mutate();
            }}
          >
            {test.isPending ? "Enviando…" : "Enviar teste"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RepliesTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "agenda-messages", "replies"],
    queryFn: () => gpsDriveAgendaMessagesService.recentReplies(100),
    refetchInterval: 60_000,
  });
  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError) return <p className="text-sm text-destructive">Não foi possível carregar as respostas.</p>;
  if (!data?.length) {
    return (
      <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
        Nenhuma resposta ainda. Quando um cliente responder uma mensagem, ela aparece aqui e no agendamento.
      </p>
    );
  }
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="divide-y p-0">
        {data.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.clientName}</span>
                {r.tag && <Badge className="border-transparent bg-sky-500/15 text-sky-700 hover:bg-sky-500/15 dark:text-sky-300">{r.tag}</Badge>}
                {r.score ? <span className="text-xs">{"⭐".repeat(r.score)}</span> : null}
              </div>
              <p className="mt-0.5 break-words text-sm text-muted-foreground">“{r.text}”</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {r.kind ? AGENDA_MESSAGE_KIND_LABELS[r.kind] : "Mensagem"} · {r.appointmentTitle}
                {r.appointmentStart ? ` em ${fmt(r.appointmentStart)}` : ""}
              </p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{fmt(r.receivedAt)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
